import React, { FC, useState } from 'react'
import { Spin, Alert } from 'antd'
import Layout from '@/Layouts/Default'
import { ICustomerWithPricing } from '@/supabase/Customer/customer.interface'
import { IPageState } from './interfaces'
import { DefaultPricingCard, RegularCustomerTable } from './components'
import {
  useDefaultCustomer,
  useRegularCustomersExcludingDefault,
  useUpdateRegularCustomer,
  useUpdateDefaultCustomer,
  useCreateRegularCustomer,
  useDeleteRegularCustomer,
  useUpdateCustomerRanking,
} from './hooks'

export const CustomerManagement: FC = () => {
  const [state, setState] = useState<IPageState>({
    isFormVisible: false,
    selectedCustomer: null,
    isLoading: false,
  })

  // Data hooks
  const {
    data: defaultCustomer,
    isLoading: defaultLoading,
    error: defaultError,
  } = useDefaultCustomer()

  const {
    data: regularCustomers = [],
    isLoading: customersLoading,
    error: customersError,
  } = useRegularCustomersExcludingDefault()

  // Mutation hooks
  const updateCustomerMutation = useUpdateRegularCustomer()
  const updateDefaultCustomerMutation = useUpdateDefaultCustomer()
  const createCustomerMutation = useCreateRegularCustomer()
  const deleteCustomerMutation = useDeleteRegularCustomer()
  const updateRankingMutation = useUpdateCustomerRanking()

  // Event handlers
  const handleEditCustomer = async (
    customer: ICustomerWithPricing,
    formData?: any
  ) => {
    if (formData) {
      // Handle inline edit save
      try {
        setState(prev => ({ ...prev, isLoading: true }))

        // Check if this is the default customer
        if (customer.id === 'default') {
          await updateDefaultCustomerMutation.mutateAsync(formData)
        } else {
          await updateCustomerMutation.mutateAsync({ customer, formData })
        }
      } finally {
        setState(prev => ({ ...prev, isLoading: false }))
      }
    } else {
      // Handle form edit (fallback - should not be used anymore)
      setState(prev => ({
        ...prev,
        selectedCustomer: customer,
        isFormVisible: true,
      }))
    }
  }

  const handleCreateCustomer = async (formData?: any) => {
    if (formData) {
      // Handle inline create save
      try {
        setState(prev => ({ ...prev, isLoading: true }))
        await createCustomerMutation.mutateAsync(formData)
      } finally {
        setState(prev => ({ ...prev, isLoading: false }))
      }
    } else {
      // Handle form create (show form) - for default customer only
      setState(prev => ({
        ...prev,
        selectedCustomer: null,
        isFormVisible: true,
      }))
    }
  }

  const handleUpdateRanking = async (
    customerId: string,
    direction: 'up' | 'down'
  ) => {
    try {
      setState(prev => ({ ...prev, isLoading: true }))
      await updateRankingMutation.mutateAsync({
        customers: regularCustomers,
        customerId,
        direction,
      })
    } finally {
      setState(prev => ({ ...prev, isLoading: false }))
    }
  }

  const handleDeleteCustomer = async (customerId: string) => {
    try {
      setState(prev => ({ ...prev, isLoading: true }))
      await deleteCustomerMutation.mutateAsync(customerId)
    } finally {
      setState(prev => ({ ...prev, isLoading: false }))
    }
  }

  // Loading state
  const isLoading =
    defaultLoading ||
    customersLoading ||
    state.isLoading ||
    updateCustomerMutation.isPending ||
    updateDefaultCustomerMutation.isPending ||
    createCustomerMutation.isPending ||
    deleteCustomerMutation.isPending ||
    updateRankingMutation.isPending

  // Error handling
  const hasError = defaultError || customersError
  if (hasError) {
    return (
      <Layout>
        <div className='p-6'>
          <Alert
            message='Error Occurred'
            description='Unable to load data. Please try again.'
            type='error'
            showIcon
          />
        </div>
      </Layout>
    )
  }

  return (
    <Layout>
      <div className='min-h-screen'>
        <div className='w-full mx-auto p-4 px-12'>
          {/* Loading Overlay */}
          {isLoading && (
            <div className='fixed inset-0 bg-black bg-opacity-20 flex items-center justify-center z-50'>
              <Spin size='large' />
            </div>
          )}

          {/* Page Header */}
          <div className='mb-8'>
            <h1 className='text-3xl font-bold text-gray-900 mb-2'>
              Customer Management & Pricing
            </h1>
          </div>

          {/* Default Pricing Card */}
          <DefaultPricingCard
            defaultCustomer={defaultCustomer || null}
            onEdit={handleEditCustomer}
            loading={defaultLoading}
          />

          {/* Regular Customers Table */}
          <RegularCustomerTable
            customers={regularCustomers}
            onEditCustomer={handleEditCustomer}
            onCreateCustomer={handleCreateCustomer}
            onUpdateRanking={handleUpdateRanking}
            onDeleteCustomer={handleDeleteCustomer}
            loading={customersLoading}
          />
        </div>
      </div>
    </Layout>
  )
}

export default CustomerManagement
